import type { OrderItem } from '../../../types';
import { bookingDays, calendarDaysUntil } from '../../../utils/dateUtils';
import { hasNoPaymentObligation, paymentStatusOf } from '../../../utils/paymentAmount';
import { reviewBadgeKeyOf } from '../../../utils/orderStatusFilter';
import { SERVICE_TYPE_ICON, serviceTypeChipClass } from '../../../utils/serviceTypeColor';

interface Props {
  order: OrderItem;
  onClick: (order: OrderItem) => void;
  onEdit?: (order: OrderItem) => void;
  /** 传服务类型名称：/orders 的 serviceType 筛选按名称匹配（orders.service_type 存的就是名称） */
  onServiceTypeClick?: (serviceTypeName: string) => void;
}

const ACTIVE_STATUSES = new Set(['未执行', '执行中']);

// 判断今日到达状态
function calcTodayArrival(arrivalDateTime: string | null): { isToday: boolean; timeStr: string; isPast: boolean } | null {
  if (!arrivalDateTime) return null;
  const arr = new Date(arrivalDateTime);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today.getTime() + 86400000);
  if (arr < today || arr >= todayEnd) return null;
  const timeStr = arr.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  const isPast = arr < new Date();
  return { isToday: true, timeStr, isPast };
}

interface UpcomingBadgeProps { days: number }
function UpcomingBadge({ days }: UpcomingBadgeProps) {
  if (days === 0) return (
    <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-600 whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
      今日
    </span>
  );
  if (days === 1) return (
    <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
      明日
    </span>
  );
  return (
    <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
      {days}天后
    </span>
  );
}

const ORDER_STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  '未执行': { bg: 'bg-amber-50 text-amber-600 border border-amber-200',   text: '' },
  '执行中': { bg: 'bg-sky-50 text-sky-600 border border-sky-200',         text: '' },
  '已完成': { bg: 'bg-teal-50 text-teal-600 border border-teal-200',      text: '' },
  '已取消': { bg: 'bg-rose-50 text-rose-500 border border-rose-200',      text: '' },
  '已归档': { bg: 'bg-gray-100 text-gray-500 border border-gray-200',     text: '' },
};

// 服务类型的取色与取图标见 utils/serviceTypeColor.ts：名字是用户自填的自由文本，不做名字表。

const PAYMENT_LABELS: Record<string, { label: string; cls: string; icon: string }> = {
  '未付款':   { label: '未付款',   cls: 'bg-orange-50 text-red-700 border border-orange-200',  icon: 'ri-error-warning-line' },
  '部分付款': { label: '部分付款', cls: 'bg-sky-50 text-sky-600 border border-sky-200',         icon: 'ri-wallet-3-line' },
  '已结清':   { label: '已结清',   cls: 'bg-teal-50 text-teal-600 border border-teal-200',      icon: 'ri-checkbox-circle-line' },
};
// 0 元单（先占档期后定价）没有收款义务：付款徽章整块不显示（`hasNoPaymentObligation`），
// 既不标「未付款」（它不欠钱），也不备一条「无金额」徽章——多一个档位就多一处对不齐。

const REVIEW_STATUS: Record<string, { label: string; icon: string; cls: string }> = {
  '待审核': { label: '待审核', icon: 'ri-shield-line', cls: 'text-amber-500' },
  '已通过': { label: '已通过', icon: 'ri-shield-check-line', cls: 'text-teal-500' },
  '已拒绝': { label: '已拒绝', icon: 'ri-shield-cross-line', cls: 'text-rose-500' },
};

function formatDateChinese(d: string | null): string {
  if (!d) return '-';
  const date = new Date(d + 'T00:00:00');
  return `${date.getFullYear()}年${String(date.getMonth() + 1).padStart(2, '0')}月${String(date.getDate()).padStart(2, '0')}日`;
}

function calcDays(start: string | null, end: string | null): string {
  const days = bookingDays(start, end);
  return days === null ? '-' : `${days}天`;
}

function AvatarCell({ name, avatar }: { name: string | null; avatar: string | null }) {
  const letter = (name ?? '?').charAt(0).toUpperCase();
  const colors = ['bg-teal-500', 'bg-amber-500', 'bg-sky-500', 'bg-rose-500', 'bg-emerald-500', 'bg-violet-500'];
  const idx = (name ?? '').charCodeAt(0) % colors.length;
  if (avatar) {
    return (
      <div className="w-7 h-7 rounded-full overflow-hidden shrink-0">
        <img src={avatar} alt={name ?? ''} className="w-full h-full object-cover object-top" />
      </div>
    );
  }
  return (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0 ${colors[idx]}`}>
      {letter}
    </div>
  );
}

export default function OrderCard({ order, onClick, onEdit, onServiceTypeClick }: Props) {
  const statusStyle = ORDER_STATUS_STYLES[order.orderStatus ?? ''] ?? { bg: 'bg-gray-100 text-gray-500 border border-gray-200', text: '' };
  // 判定走 web 端唯一实现（与后端 computePaymentStatus 同式），本卡不再留一份三元副本
  const payKey = paymentStatusOf(order.orderAmount, order.paidAmount);
  // 0 元占位单不出徽章：它不欠钱，出一条「未付款」就是把人往催收单里带
  const payInfo = hasNoPaymentObligation(order.orderAmount, order.paidAmount)
    ? null
    : PAYMENT_LABELS[payKey] ?? PAYMENT_LABELS['未付款'];
  const reviewKey = reviewBadgeKeyOf(order.reviewStatus, order.orderStatus);
  const reviewInfo = reviewKey === null ? null : REVIEW_STATUS[reviewKey] ?? null;

  const isExpense = (order.orderCategory ?? '').includes('支出');
  const customerName = order.customer?.fullName ?? order.contactPerson ?? '未知客户';
  const phone = order.contactPhone ?? order.customer?.phone;

  const serviceTypeName = order.serviceType?.name ?? '';
  const stCls = serviceTypeChipClass(serviceTypeName);

  // 临近提醒
  const isActive = ACTIVE_STATUSES.has(order.orderStatus ?? '');
  const daysUntil = isActive ? calendarDaysUntil(order.orderStartDate) : null;
  const isUpcoming = daysUntil !== null && daysUntil >= 0 && daysUntil <= 3;

  const urgentBorderClass = isUpcoming
    ? daysUntil === 0 ? 'border-rose-300' : daysUntil === 1 ? 'border-orange-300' : 'border-amber-200'
    : 'border-gray-100';

  // 今日到达检测
  const todayArrival = calcTodayArrival(order.arrivalDateTime);
  const isMultiDay = order.orderEndDate && order.orderEndDate !== order.orderStartDate;

  return (
    <div
      onClick={() => onClick(order)}
      className={`bg-white border rounded-xl overflow-hidden cursor-pointer hover:border-teal-200 transition-all group relative ${urgentBorderClass}`}
    >
      {/* 临近提醒色条 */}
      {isUpcoming && (
        <div className={`h-0.5 w-full ${daysUntil === 0 ? 'bg-rose-400' : daysUntil === 1 ? 'bg-orange-400' : 'bg-amber-300'}`} />
      )}

      {/* ── Section 1: type + category + status ── */}
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            {/* 收入 / 支出 */}
            <div className={`flex items-center gap-1 text-xs font-medium shrink-0 ${isExpense ? 'text-rose-500' : 'text-emerald-500'}`}>
              <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 shrink-0 ${isExpense ? 'border-rose-400' : 'border-emerald-400'}`}>
                <i className={`${isExpense ? 'ri-subtract-line' : 'ri-add-line'} text-xs`} />
              </div>
              {isExpense ? '支出' : '收入'}
            </div>
            {/* 服务类型 - 带颜色 + 图标 */}
            {serviceTypeName && (
              <span
                onClick={onServiceTypeClick && serviceTypeName ? (e) => { e.stopPropagation(); onServiceTypeClick(serviceTypeName); } : undefined}
                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full shrink-0 truncate max-w-[100px] ${stCls} ${onServiceTypeClick ? 'cursor-pointer hover:opacity-70 transition-opacity' : ''}`}
              >
                <i className={`${SERVICE_TYPE_ICON} text-xs shrink-0`} />
                {serviceTypeName}
              </span>
            )}
          </div>
          {/* 订单状态 */}
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium shrink-0 whitespace-nowrap ${statusStyle.bg}`}>
            {order.orderStatus ?? '未知'}
          </span>
        </div>

        {/* Order number */}
        <div className="flex items-center gap-1.5 mb-3">
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-file-list-line text-gray-400 text-sm" />
          </div>
          <span className="text-xs font-mono text-gray-500 truncate">
            {order.orderNumber ?? order.id.slice(0, 12).toUpperCase()}
          </span>
        </div>

        {/* Customer */}
        <div className="flex items-center gap-2 mb-2">
          <AvatarCell name={customerName} avatar={order.customer?.avatar ?? null} />
          <span className="text-sm text-gray-700 truncate">
            客户：<span className="font-medium">{customerName}</span>
          </span>
        </div>

        {/* Phone - 可拨打 */}
        {phone && (
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 flex items-center justify-center shrink-0">
              <i className="ri-smartphone-line text-gray-400 text-sm" />
            </div>
            <a
              href={`tel:${phone}`}
              onClick={e => e.stopPropagation()}
              className="text-sm text-teal-500 hover:text-teal-600 hover:underline truncate cursor-pointer transition-colors"
            >
              {phone}
            </a>
          </div>
        )}

        {/* Activity date + 临近提醒 */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className={`ri-calendar-line text-sm ${isUpcoming ? (daysUntil === 0 ? 'text-rose-400' : daysUntil === 1 ? 'text-orange-400' : 'text-amber-400') : 'text-gray-400'}`} />
          </div>
          <span className="text-sm text-gray-600">
            {isMultiDay
              ? `${formatDateChinese(order.orderStartDate)} ~ ${formatDateChinese(order.orderEndDate)}`
              : `活动日期：${formatDateChinese(order.orderStartDate)}`}
          </span>
          {isUpcoming && <UpcomingBadge days={daysUntil!} />}
        </div>

        {/* 预计到达时间 - 今日特别高亮 */}
        {todayArrival && (
          <div className={`flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg ${todayArrival.isPast ? 'bg-gray-50' : 'bg-rose-50'}`}>
            <div className="w-4 h-4 flex items-center justify-center shrink-0">
              <i className={`ri-map-pin-time-line text-sm ${todayArrival.isPast ? 'text-gray-400' : 'text-rose-500'}`} />
            </div>
            <span className={`text-xs font-medium ${todayArrival.isPast ? 'text-gray-500' : 'text-rose-600'}`}>
              今日到达：{todayArrival.timeStr}
            </span>
            {!todayArrival.isPast && (
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse ml-auto shrink-0" />
            )}
            {todayArrival.isPast && (
              <span className="text-[10px] text-gray-400 ml-auto shrink-0 whitespace-nowrap">已过</span>
            )}
          </div>
        )}

        {/* Reserved days */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-time-line text-gray-400 text-sm" />
          </div>
          <span className="text-sm text-gray-600">
            预定天数：{calcDays(order.orderStartDate, order.orderEndDate)}
          </span>
        </div>

        {/* Activity address */}
        {order.activityAddress && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center shrink-0">
              <i className="ri-map-pin-line text-gray-400 text-sm" />
            </div>
            <span className="text-sm text-gray-500 truncate" title={order.activityAddress}>
              {order.activityAddress}
            </span>
          </div>
        )}
      </div>

      {/* ── Section 2: payment + review ── */}
      {(payInfo || reviewInfo) && (
        <>
          <div className="mx-4 border-t border-gray-100" />
          <div className="px-4 py-2.5 flex items-center justify-between min-h-[40px]">
            {payInfo ? (
              <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${payInfo.cls}`}>
                <i className={`${payInfo.icon} text-xs shrink-0`} />
                {payInfo.label}
              </span>
            ) : (
              // 徽章缺席也要占位，否则右侧的审核状态会被 justify-between 推到左边
              <span aria-hidden="true" />
            )}
            {reviewInfo && (
              <div className={`flex items-center gap-1 text-xs font-medium ${reviewInfo.cls}`}>
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className={`${reviewInfo.icon} text-sm`} />
                </div>
                {reviewInfo.label}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Divider ── */}
      <div className="mx-4 border-t border-gray-100" />

      {/* ── Footer: view detail + edit ── */}
      <div className="px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs text-teal-500 group-hover:text-teal-600 transition-colors">
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-eye-line text-sm" />
          </div>
          查看详情
        </div>
        {order.canEditOrder === true && (
          <button
            onClick={e => { e.stopPropagation(); onEdit?.(order); }}
            className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-teal-500 rounded-lg cursor-pointer transition-colors"
          >
            <i className="ri-pencil-line text-sm" />
          </button>
        )}
      </div>
    </div>
  );
}
